import { useEffect, useState, useMemo } from "react";
import SidebarAdmin from "./sidebar-admin";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminHeader from "./AdminHeader";
import { API_URL } from "../config";

interface Country {
  id: number;
  name?: string;
  name_en: string;
  name_ar: string;
  code?: string;
  is_active: boolean;
}

function SkeletonLoader() {
  return (
    <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "20px 20px", boxSizing: "border-box" }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* Header Skeleton */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <div style={{ 
              width: 180, 
              height: 28,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
              backgroundSize: "200% 100%",
              borderRadius: 6,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{ 
              width: 60, 
              height: 20,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
              backgroundSize: "200% 100%",
              borderRadius: 12,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
          <div style={{ 
            width: 280, 
            height: 18,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
        </div>
      </div>

      {/* Main Card Skeleton */}
      <div style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        marginTop: "15px",
      }}>
        {/* Filter Bar Skeleton */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #edf2f7" }}>
          <div className="row g-3 align-items-center">
            <div className="col-12 col-md-6 col-lg-5">
              <div style={{ 
                width: "100%", 
                height: 42,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
          </div>
        </div>

        {/* Table Header Skeleton */}
        <div style={{ padding: "12px 16px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <div className="row">
            {["Name", "Name (EN)", "Name (AR)", "Code", "Status", "Actions"].map((_, i) => (
              <div key={i} className="col" style={{ padding: "0 8px" }}>
                <div style={{ 
                  width: i === 0 ? 70 : i === 1 ? 70 : i === 2 ? 70 : i === 3 ? 40 : i === 4 ? 50 : 50,
                  height: 14,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
            ))}
          </div>
        </div>

        {/* Table Rows Skeleton */}
        {[...Array(8)].map((_, rowIndex) => (
          <div 
            key={rowIndex} 
            style={{ 
              padding: "14px 16px", 
              borderBottom: rowIndex < 7 ? "1px solid #f1f5f9" : "none",
              display: "flex",
              alignItems: "center"
            }}
          >
            {[...Array(6)].map((_, colIndex) => (
              <div key={colIndex} style={{ flex: 1, padding: "0 8px" }}>
                <div style={{ 
                  width: colIndex === 0 ? 100 : colIndex === 1 ? 100 : colIndex === 2 ? 100 : colIndex === 3 ? 50 : colIndex === 4 ? 80 : 80,
                  height: 14,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
            ))}
          </div>
        ))}

        {/* Pagination Skeleton */}
        <div style={{ 
          padding: "12px 20px", 
          borderTop: "1px solid #edf2f7",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <div style={{ 
            width: 200, 
            height: 16,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
          <div style={{ display: "flex", gap: "6px" }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} style={{ 
                width: i === 0 || i === 4 ? 70 : 32,
                height: 32,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 6,
                animation: "shimmer 1.5s infinite"
              }}></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const ManageCountries = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState<"all" | "active" | "inactive">("all");

  // PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const handleLogout = () => console.log("User logged out");

  const [formData, setFormData] = useState({
    name: "",
    name_en: "",
    name_ar: "",
    code: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Récupération du token
  const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const API_URL_1 = `${API_URL}/countries`;

  // 1. Fetch Countries
  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL_1, config_token);
      const data = response.data.data ? response.data.data : response.data;
      setCountries(data);
    } catch (error) {
      console.error("Error fetching countries:", error);
      toast.error("Error fetching countries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  // 2. Filtre de recherche local
  const filteredCountries = useMemo(() => {
    return countries.filter((country) => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        (country.name && country.name.toLowerCase().includes(term)) ||
        (country.name_en && country.name_en.toLowerCase().includes(term)) ||
        (country.name_ar && country.name_ar.includes(term)) ||
        (country.code && country.code.toLowerCase().includes(term));

      const matchesVisibility =
        visibilityFilter === "all"
          ? true
          : visibilityFilter === "active"
          ? country.is_active
          : !country.is_active;

      return matchesSearch && matchesVisibility;
    });
  }, [countries, searchTerm, visibilityFilter]);

  // Réinitialiser la page à 1 lorsqu'un filtre ou un terme de recherche change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, visibilityFilter]);

  // 3. Calculs pour la pagination
  const totalPages = Math.ceil(filteredCountries.length / itemsPerPage);
  
  const paginatedCountries = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredCountries.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredCountries, currentPage, itemsPerPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // 4. Modals & Handlers
  const handleOpenCreateModal = () => {
    setSelectedCountry(null);
    setFormData({ name: "", name_en: "", name_ar: "", code: "" });
    setShowFormModal(true);
  };

  const handleOpenEditModal = (country: Country) => {
    setSelectedCountry(country);
    setFormData({
      name: country.name || "",
      name_en: country.name_en || "",
      name_ar: country.name_ar || "",
      code: country.code || "",
    });
    setShowFormModal(true);
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name_en.trim() || !formData.name_ar.trim()) {
      toast.warning("English and Arabic names are required");
      return;
    }

    const payload = {
      ...formData,
      name: formData.name.trim() ? formData.name : formData.name_en,
    };

    try {
      setSubmitting(true);
      if (selectedCountry) {
        await axios.put(`${API_URL_1}/${selectedCountry.id}`, payload, config_token);
        toast.success("Country updated successfully");
      } else {
        await axios.post(API_URL_1, payload, config_token);
        toast.success("Country created successfully");
      }
      setShowFormModal(false);
      fetchCountries();
    } catch (error: any) {
      console.error("Error saving country:", error);
      toast.error(error.response?.data?.message || "Error saving country");
    } finally {
      setSubmitting(false);
    }
  };

  // 5. Toggle Status Active
  const handleToggleActive = async (country: Country) => {
    try {
      await axios.patch(`${API_URL_1}/${country.id}/toggle-active`, {}, config_token);
      setCountries((prev) =>
        prev.map((c) => (c.id === country.id ? { ...c, is_active: !c.is_active } : c))
      );
      toast.success(`Country ${!country.is_active ? "activated" : "deactivated"}`);
    } catch (error) {
      console.error("Error toggling active state:", error);
      toast.error("Error updating status");
    }
  };

  // 6. Delete Country
  const handleDeleteClick = (country: Country) => {
    setSelectedCountry(country);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCountry) return;

    try {
      await axios.delete(`${API_URL}/countries/${selectedCountry.id}`, config_token);
      setCountries((prev) => prev.filter((c) => c.id !== selectedCountry.id));
      toast.success("Country deleted successfully");
    } catch (error) {
      console.error("Error deleting country:", error);
      toast.error("Error deleting country");
    } finally {
      setShowDeleteModal(false);
      setSelectedCountry(null);
    }
  };

  // Helper pour générer la liste des numéros de pages avec ellipses ("...")
  const getPaginationRange = (currentPage: number, totalPages: number) => {
    const delta = 1;
    const range: (number | string)[] = [];

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        range.push(i);
      } else if (
        range[range.length - 1] !== "..." &&
        (i < currentPage - delta || i > currentPage + delta)
      ) {
        range.push("...");
      }
    }

    return range;
  };

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        padding: "20px 20px",
        boxSizing: "border-box",
        color: "#1e293b",
      }}
    >
      {/* SECTION EN-TÊTE */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h1
              style={{
                color: "#294b5a",
                fontSize: "22px",
                fontWeight: 700,
                margin: 0,
                letterSpacing: "-0.02em",
              }}
            >
              Manage Countries
            </h1>
            <span
              style={{
                backgroundColor: "#294b5a",
                color: "#d3be76",
                fontSize: "11px",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "12px",
                letterSpacing: "0.5px",
              }}
            >
              System
            </span>
          </div>
          <p
            style={{
              color: "#64748b",
              fontSize: "13.5px",
              margin: 0,
              fontWeight: 400,
            }}
          >
            Overview and configuration of{" "}
            <strong style={{ color: "#294b5a" }}>
              {filteredCountries.length}
            </strong>{" "}
            of <strong>{countries.length}</strong> total countries.
          </p>
        </div>

        {/* Bouton Add Country */}
        <button
          onClick={handleOpenCreateModal}
          style={{
            backgroundColor: "#294b5a",
            color: "#ffffff",
            border: "none",
            borderRadius: "8px",
            padding: "10px 18px",
            fontSize: "13.5px",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "0 2px 8px rgba(41, 75, 90, 0.25)",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "#1e3743";
            e.currentTarget.style.boxShadow =
              "0 4px 12px rgba(41, 75, 90, 0.35)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "#294b5a";
            e.currentTarget.style.boxShadow =
              "0 2px 8px rgba(41, 75, 90, 0.25)";
          }}
        >
          <i
            className="fa-solid fa-plus"
            style={{ color: "#fff", fontSize: "13px" }}
          ></i>
          <span>Add Country</span>
        </button>
      </div>

      {/* BLOC PRINCIPAL CONTENANT FILTRES + TABLE */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 16px -2px rgba(0,0,0,0.03)",
          overflow: "hidden",
          marginTop: "15px",
        }}
      >
        {/* BARRE DE FILTRES */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #edf2f7",
            backgroundColor: "#ffffff",
          }}
        >
          <div className="row g-3 align-items-center">
            <div className="col-12 col-md-6 col-lg-4">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder="Search by name, English, Arabic or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px 14px 9px 38px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "13px",
                    outline: "none",
                    color: "#0f172a",
                    backgroundColor: "#f8fafc",
                    transition: "all 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#294b5a";
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.boxShadow = "0 0 0 3px rgba(41, 75, 90, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#cbd5e1";
                    e.target.style.backgroundColor = "#f8fafc";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "13px",
                    color: "#94a3b8",
                  }}
                ></i>
              </div>
            </div>

            <div className="col-12 col-md-4 col-lg-2">
              <select
                value={visibilityFilter}
                onChange={(e) => setVisibilityFilter(e.target.value as any)}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: "#334155",
                  outline: "none",
                  backgroundColor: "#f8fafc",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#294b5a";
                  e.target.style.backgroundColor = "#ffffff";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#cbd5e1";
                  e.target.style.backgroundColor = "#f8fafc";
                }}
              >
                <option value="all">All Visibility</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* TABLEAU */}
        {loading ? (
          <SkeletonLoader />
        ) : (
          <>
            <div className="table-responsive" style={{ margin: 0 }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  textAlign: "left",
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <th
                      style={{
                        padding: "12px 20px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        width: "20%",
                      }}
                    >
                      Name
                    </th>
                    <th
                      style={{
                        padding: "12px 20px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        width: "20%",
                      }}
                    >
                      Name (EN)
                    </th>
                    <th
                      style={{
                        padding: "12px 20px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        width: "20%",
                      }}
                    >
                      Name (AR)
                    </th>
                    <th
                      style={{
                        padding: "12px 20px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        textAlign: "center",
                        width: "10%",
                      }}
                    >
                      Code
                    </th>
                    <th
                      style={{
                        padding: "12px 20px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        textAlign: "center",
                        width: "15%",
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        padding: "12px 20px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        textAlign: "right",
                        width: "15%",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCountries.length > 0 ? (
                    paginatedCountries.map((country) => (
                      <tr
                        key={country.id}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          transition: "background-color 0.15s ease",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.backgroundColor = "#fafaf8")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.backgroundColor = "transparent")
                        }
                      >
                        {/* Default Name */}
                        <td
                          style={{
                            padding: "14px 20px",
                            fontSize: "13.5px",
                            fontWeight: 600,
                            color: "#0f172a",
                          }}
                        >
                          {country.name || country.name_en}
                        </td>

                        {/* Name EN */}
                        <td
                          style={{
                            padding: "14px 20px",
                            fontSize: "13.5px",
                            color: "#475569",
                          }}
                        >
                          {country.name_en}
                        </td>

                        {/* Name AR */}
                        <td
                          style={{
                            padding: "14px 20px",
                            fontSize: "14px",
                            color: "#334155",
                            fontWeight: 500,
                          }}
                          dir="rtl"
                        >
                          {country.name_ar}
                        </td>

                        {/* Code */}
                        <td
                          style={{
                            padding: "14px 20px",
                            textAlign: "center",
                          }}
                        >
                          <span
                            style={{
                              padding: "3px 8px",
                              backgroundColor: "#f1f5f9",
                              color: "#294b5a",
                              borderRadius: "6px",
                              fontSize: "11px",
                              fontWeight: 700,
                              letterSpacing: "0.5px",
                              border: "1px solid #cbd5e1",
                            }}
                          >
                            {country.code || "-"}
                          </span>
                        </td>

                        {/* Status */}
                        <td
                          style={{
                            padding: "14px 20px",
                            textAlign: "center",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "3px 10px",
                              borderRadius: "20px",
                              fontSize: "12px",
                              fontWeight: 600,
                              backgroundColor: country.is_active
                                ? "#f0fdf4"
                                : "#fef2f2",
                              color: country.is_active ? "#15803d" : "#b91c1c",
                              border: `1px solid ${
                                country.is_active ? "#bbf7d0" : "#fecaca"
                              }`,
                            }}
                          >
                            <span
                              style={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                backgroundColor: country.is_active
                                  ? "#16a34a"
                                  : "#dc2626",
                              }}
                            ></span>
                            {country.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>

                        {/* Actions */}
                        <td
                          style={{
                            padding: "14px 20px",
                            textAlign: "right",
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            {/* Toggle Status Icon Button */}
                            <button
                              onClick={() => handleToggleActive(country)}
                              style={{
                                border: "1px solid #e2e8f0",
                                backgroundColor: "#ffffff",
                                color: country.is_active ? "#dc2626" : "#16a34a",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  country.is_active ? "#fef2f2" : "#f0fdf4";
                                e.currentTarget.style.borderColor =
                                  country.is_active ? "#fecaca" : "#bbf7d0";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#ffffff";
                                e.currentTarget.style.borderColor = "#e2e8f0";
                              }}
                              title={
                                country.is_active ? "Deactivate" : "Activate"
                              }
                            >
                              <i
                                className={`fa-solid ${
                                  country.is_active
                                    ? "fa-ban"
                                    : "fa-circle-check"
                                }`}
                                style={{ fontSize: "13px" }}
                              ></i>
                            </button>

                            {/* Edit Button */}
                            <button
                              onClick={() => handleOpenEditModal(country)}
                              style={{
                                border: "1px solid #e2e8f0",
                                backgroundColor: "#ffffff",
                                color: "#294b5a",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f8fafc";
                                e.currentTarget.style.borderColor = "#cbd5e1";
                                e.currentTarget.style.color = "#d3be76";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#ffffff";
                                e.currentTarget.style.borderColor = "#e2e8f0";
                                e.currentTarget.style.color = "#294b5a";
                              }}
                              title="Edit"
                            >
                              <i
                                className="fa-solid fa-pen-to-square"
                                style={{ fontSize: "13px" }}
                              ></i>
                            </button>

                            {/* Delete Button */}
                            <button
                              onClick={() => handleDeleteClick(country)}
                              style={{
                                border: "1px solid #e2e8f0",
                                backgroundColor: "#ffffff",
                                color: "#64748b",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#fef2f2";
                                e.currentTarget.style.borderColor = "#fecaca";
                                e.currentTarget.style.color = "#dc2626";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#ffffff";
                                e.currentTarget.style.borderColor = "#e2e8f0";
                                e.currentTarget.style.color = "#64748b";
                              }}
                              title="Delete"
                            >
                              <i
                                className="fa-solid fa-trash-can"
                                style={{ fontSize: "13px" }}
                              ></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          textAlign: "center",
                          padding: "48px 20px",
                          color: "#64748b",
                          fontSize: "13.5px",
                        }}
                      >
                        <i
                          className="fa-solid fa-earth-americas mb-2"
                          style={{
                            fontSize: "24px",
                            color: "#cbd5e1",
                            display: "block",
                          }}
                        ></i>
                        No countries found matching your criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* BARRE DE PAGINATION */}
            {filteredCountries.length > 0 && (
              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid #edf2f7",
                  backgroundColor: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  Showing{" "}
                  <strong>
                    {Math.min(
                      (currentPage - 1) * itemsPerPage + 1,
                      filteredCountries.length
                    )}
                  </strong>{" "}
                  to{" "}
                  <strong>
                    {Math.min(
                      currentPage * itemsPerPage,
                      filteredCountries.length
                    )}
                  </strong>{" "}
                  of <strong>{filteredCountries.length}</strong> entries
                </div>

                {totalPages > 1 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {/* Bouton Précédent */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      style={{
                        padding: "6px 12px",
                        fontSize: "12.5px",
                        fontWeight: "600",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        backgroundColor: "#ffffff",
                        color: currentPage === 1 ? "#94a3b8" : "#294b5a",
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      Previous
                    </button>

                    {/* Numéros de page avec Ellipses */}
                    {getPaginationRange(currentPage, totalPages).map((item, index) => {
                      if (item === "...") {
                        return (
                          <span
                            key={`dots-${index}`}
                            style={{
                              padding: "0 6px",
                              fontSize: "13px",
                              color: "#94a3b8",
                              fontWeight: "600",
                              userSelect: "none",
                            }}
                          >
                            ...
                          </span>
                        );
                      }

                      const pageNum = item as number;
                      const isActive = currentPage === pageNum;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          style={{
                            width: "32px",
                            height: "32px",
                            fontSize: "12.5px",
                            fontWeight: "600",
                            borderRadius: "6px",
                            border: isActive ? "none" : "1px solid #cbd5e1",
                            backgroundColor: isActive ? "#294b5a" : "#ffffff",
                            color: isActive ? "#ffffff" : "#334155",
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {/* Bouton Suivant */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: "6px 12px",
                        fontSize: "12.5px",
                        fontWeight: "600",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        backgroundColor: "#ffffff",
                        color: currentPage === totalPages ? "#94a3b8" : "#294b5a",
                        cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal: Create / Edit Country */}
      {showFormModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "16px",
            animation: "fadeIn 0.2s ease-out",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "520px",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
            }}
          >
            {/* MODAL HEADER */}
            <div
              style={{
                backgroundColor: "#294b5a",
                padding: "20px 24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "2px solid #d3be76",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "8px",
                    backgroundColor: "rgba(211, 190, 118, 0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className={`fa-solid ${
                      selectedCountry ? "fa-pen-to-square" : "fa-earth-americas"
                    }`}
                    style={{ color: "#d3be76", fontSize: "16px" }}
                  ></i>
                </div>
                <div>
                  <h2
                    style={{
                      fontSize: "17px",
                      fontWeight: "700",
                      color: "#ffffff",
                      margin: 0,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {selectedCountry ? "Edit Country" : "Add New Country"}
                  </h2>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "rgba(255,255,255,0.7)",
                      margin: "2px 0 0 0",
                    }}
                  >
                    {selectedCountry
                      ? "Update country details and configuration"
                      : "Enter localization details for the new country"}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowFormModal(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "rgba(255,255,255,0.6)",
                  fontSize: "18px",
                  cursor: "pointer",
                  width: "32px",
                  height: "32px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.color = "#ffffff";
                  e.currentTarget.style.backgroundColor =
                    "rgba(255,255,255,0.1)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = "rgba(255,255,255,0.6)";
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            {/* FORM BODY */}
            <form onSubmit={handleSubmitForm} style={{ padding: "24px" }}>
              {/* Nom en Anglais */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#294b5a",
                    marginBottom: "6px",
                  }}
                >
                  Name (English) <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.name_en}
                  onChange={(e) =>
                    setFormData({ ...formData, name_en: e.target.value })
                  }
                  placeholder="e.g. Oman, France..."
                  required
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    fontSize: "13.5px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#f8fafc",
                    color: "#0f172a",
                    outline: "none",
                    transition: "all 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#294b5a";
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(41, 75, 90, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#cbd5e1";
                    e.target.style.backgroundColor = "#f8fafc";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              {/* Nom en Arabe */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#294b5a",
                    marginBottom: "6px",
                  }}
                >
                  Name (Arabic) <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="text"
                  dir="rtl"
                  value={formData.name_ar}
                  onChange={(e) =>
                    setFormData({ ...formData, name_ar: e.target.value })
                  }
                  placeholder="مثال: عمان، فرنسا..."
                  required
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    fontSize: "14px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#f8fafc",
                    color: "#0f172a",
                    outline: "none",
                    transition: "all 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#294b5a";
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(41, 75, 90, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#cbd5e1";
                    e.target.style.backgroundColor = "#f8fafc";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              {/* GRID LAYOUT FOR DEFAULT NAME & COUNTRY CODE */}
              <div className="row g-3" style={{ marginBottom: "24px" }}>
                {/* Nom par Défaut */}
                <div className="col-12 col-md-7">
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#294b5a",
                      marginBottom: "6px",
                    }}
                  >
                    Default Name{" "}
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#64748b",
                        fontWeight: "400",
                      }}
                    >
                      (Optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Fallback to English if empty"
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      fontSize: "13.5px",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      backgroundColor: "#f8fafc",
                      color: "#0f172a",
                      outline: "none",
                      transition: "all 0.2s ease",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#294b5a";
                      e.target.style.backgroundColor = "#ffffff";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(41, 75, 90, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#cbd5e1";
                      e.target.style.backgroundColor = "#f8fafc";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>

                {/* Code Pays */}
                <div className="col-12 col-md-5">
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#294b5a",
                      marginBottom: "6px",
                    }}
                  >
                    Code{" "}
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#64748b",
                        fontWeight: "400",
                      }}
                    >
                      (Optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value })
                    }
                    placeholder="e.g. OM, FR"
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      fontSize: "13.5px",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      backgroundColor: "#f8fafc",
                      color: "#0f172a",
                      outline: "none",
                      textTransform: "uppercase",
                      transition: "all 0.2s ease",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#294b5a";
                      e.target.style.backgroundColor = "#ffffff";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(41, 75, 90, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#cbd5e1";
                      e.target.style.backgroundColor = "#f8fafc";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
              </div>

              {/* MODAL FOOTER */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "12px",
                  paddingTop: "16px",
                  borderTop: "1px solid #f1f5f9",
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowFormModal(false)}
                  style={{
                    padding: "10px 20px",
                    fontSize: "13.5px",
                    fontWeight: "600",
                    color: "#64748b",
                    backgroundColor: "#ffffff",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8fafc";
                    e.currentTarget.style.color = "#0f172a";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = "#ffffff";
                    e.currentTarget.style.color = "#64748b";
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: "10px 24px",
                    fontSize: "13.5px",
                    fontWeight: "600",
                    color: "#ffffff",
                    backgroundColor: "#294b5a",
                    border: "none",
                    borderRadius: "8px",
                    cursor: submitting ? "not-allowed" : "pointer",
                    opacity: submitting ? 0.7 : 1,
                    boxShadow: "0 2px 6px rgba(41, 75, 90, 0.25)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.15s ease",
                  }}
                  onMouseOver={(e) => {
                    if (!submitting) {
                      e.currentTarget.style.backgroundColor = "#1e3743";
                      e.currentTarget.style.boxShadow =
                        "0 4px 10px rgba(41, 75, 90, 0.35)";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!submitting) {
                      e.currentTarget.style.backgroundColor = "#294b5a";
                      e.currentTarget.style.boxShadow =
                        "0 2px 6px rgba(41, 75, 90, 0.25)";
                    }
                  }}
                >
                  {submitting ? (
                    <>
                      <i className="fa-solid fa-circle-notch fa-spin"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i
                        className="fa-solid fa-check"
                        style={{ color: "#d3be76" }}
                      ></i>
                      {selectedCountry ? "Update Country" : "Save Country"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Confirmation de suppression */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "16px",
            animation: "fadeIn 0.2s ease-out",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "440px",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
            }}
          >
            {/* MODAL BODY */}
            <div style={{ padding: "28px 24px 20px 24px", textAlign: "center" }}>
              {/* WARNING ICON BADGE */}
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "16px",
                }}
              >
                <i
                  className="fa-solid fa-trash-can"
                  style={{ color: "#dc2626", fontSize: "22px" }}
                ></i>
              </div>

              {/* TITLE & DESCRIPTION */}
              <h2
                style={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#294b5a",
                  margin: "0 0 8px 0",
                  letterSpacing: "-0.01em",
                }}
              >
                Delete Country?
              </h2>

              <p
                style={{
                  fontSize: "13.5px",
                  color: "#64748b",
                  margin: 0,
                  lineHeight: "1.5",
                }}
              >
                Are you sure you want to delete this country? This action cannot
                be undone and may affect related data.
              </p>

              {/* TARGET COUNTRY PREVIEW CARD */}
              {selectedCountry && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px 16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    textAlign: "left",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "13.5px",
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      {selectedCountry.name_en || selectedCountry.name}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                        fontWeight: 500,
                      }}
                      dir="rtl"
                    >
                      {selectedCountry.name_ar}
                    </div>
                  </div>

                  {selectedCountry.code && (
                    <span
                      style={{
                        padding: "3px 8px",
                        backgroundColor: "#ffffff",
                        color: "#294b5a",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: 700,
                        border: "1px solid #cbd5e1",
                      }}
                    >
                      {selectedCountry.code}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* MODAL FOOTER */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "16px 24px 20px 24px",
                backgroundColor: "#ffffff",
              }}
            >
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#64748b",
                  backgroundColor: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                  e.currentTarget.style.color = "#0f172a";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.color = "#64748b";
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmDelete}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#ffffff",
                  backgroundColor: "#dc2626",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(220, 38, 38, 0.25)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#b91c1c";
                  e.currentTarget.style.boxShadow =
                    "0 4px 10px rgba(220, 38, 38, 0.35)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#dc2626";
                  e.currentTarget.style.boxShadow =
                    "0 2px 6px rgba(220, 38, 38, 0.25)";
                }}
              >
                <i
                  className="fa-solid fa-trash-can"
                  style={{ fontSize: "13px" }}
                ></i>
                Delete Country
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCountries;